import React, { useState, useEffect, useRef } from "react";
import { Box, TextField, Switch, FormControlLabel, Typography, FormControl, InputLabel, Select, MenuItem, Button, Slider } from "@mui/material";
import { SketchPicker } from "react-color";

function SettingsTab({ settings, handleSettingsChange }) {
  const [showSurveyColourPicker, setShowSurveyColourPicker] = useState({ start: false, end: false });
  const [showQuestionBackgroundPicker, setShowQuestionBackgroundPicker] = useState(false);
  const [showFontColourPicker, setShowFontColourPicker] = useState(false);

  // Helper function to convert RGBA objects to CSS rgba() format
  const rgbaToCss = (color) => {
    if (!color) return "rgba(255, 255, 255, 1)";
    if (typeof color === "string") return color;
    if (color.r !== undefined && color.g !== undefined && color.b !== undefined) {
      const alpha = color.a !== undefined ? color.a : 1;
      return `rgba(${color.r}, ${color.g}, ${color.b}, ${alpha})`;
    }
    return "rgba(255, 255, 255, 1)";
  };
  
  // Refs for color picker containers
  const startColorPickerRef = useRef(null);
  const endColorPickerRef = useRef(null);
  const questionBackgroundPickerRef = useRef(null);
  const fontColourPickerRef = useRef(null);

  // Handle click outside to close color pickers
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close start color picker
      if (showSurveyColourPicker.start && startColorPickerRef.current && !startColorPickerRef.current.contains(event.target)) {
        setShowSurveyColourPicker(prev => ({ ...prev, start: false }));
      }
      
      // Close end color picker
      if (showSurveyColourPicker.end && endColorPickerRef.current && !endColorPickerRef.current.contains(event.target)) {
        setShowSurveyColourPicker(prev => ({ ...prev, end: false }));
      }
      
      // Close question background picker
      if (showQuestionBackgroundPicker && questionBackgroundPickerRef.current && !questionBackgroundPickerRef.current.contains(event.target)) {
        setShowQuestionBackgroundPicker(false);
      }
      
      // Close font colour picker
      if (showFontColourPicker && fontColourPickerRef.current && !fontColourPickerRef.current.contains(event.target)) {
        setShowFontColourPicker(false);
      }
    };

    // Add event listener
    document.addEventListener('mousedown', handleClickOutside);
    
    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSurveyColourPicker.start, showSurveyColourPicker.end, showQuestionBackgroundPicker, showFontColourPicker]);

  const handleBackgroundImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        console.log("Background image uploaded:", reader.result);
        handleSettingsChange("backgroundImage", reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        console.log("Logo uploaded:", reader.result);
        handleSettingsChange("logo", reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <Box sx={{ display: "flex", gap: 3 }}>
      {/* Left Side: Settings Form */}
      <Box sx={{ width: "300px", flexShrink: 0 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Settings
        </Typography>

        {/* Logo Upload */}
        <Typography variant="body1" sx={{ mb: 1 }}>
          Logo
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              component="label"
              sx={{ textTransform: "none", mb: 1 }}
            >
              Upload Logo
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={handleLogoUpload}
              />
            </Button>
            {settings.logo && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2">Uploaded Logo:</Typography>
                <img
                  src={settings.logo}
                  alt="Logo"
                  style={{ maxWidth: "100px", maxHeight: "50px" }}
                />
              </Box>
            )}
          </Box>
          <FormControl fullWidth>
            <InputLabel>Logo Alignment</InputLabel>
            <Select
              value={settings.logoAlignment || "Left"}
              onChange={(e) => handleSettingsChange("logoAlignment", e.target.value)}
              label="Logo Alignment"
            >
              <MenuItem value="Left">Left</MenuItem>
              <MenuItem value="Center">Center</MenuItem>
              <MenuItem value="Right">Right</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Background Image Upload */}
        <Typography variant="body1" sx={{ mb: 1 }}>
          Background Image
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            component="label"
            sx={{ textTransform: "none", mb: 1 }}
          >
            Upload Background Image
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={handleBackgroundImageUpload}
            />
          </Button>
          {settings.backgroundImage && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2">Uploaded Background:</Typography>
              <img
                src={settings.backgroundImage}
                alt="Background"
                style={{ maxWidth: "100%", maxHeight: "100px" }}
              />
              <Button
                onClick={() => handleSettingsChange("backgroundImage", null)}
                color="error"
                sx={{ mt: 1 }}
              >
                Remove Background Image
              </Button>
            </Box>
          )}
        </Box>

        {/* Background Brightness Adjustment */}
        {settings.backgroundImage && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Background Image Brightness (%)
            </Typography>
            <Slider
              value={settings.backgroundBrightness || 100}
              onChange={(e, value) => handleSettingsChange("backgroundBrightness", value)}
              min={0}
              max={200}
              step={1}
              valueLabelDisplay="auto"
              sx={{ width: "100%" }}
            />
            <Typography variant="body2" sx={{ mt: 1 }}>
              Current brightness: {settings.backgroundBrightness || 100}%
            </Typography>
          </Box>
        )}

        {/* Redirect Link */}
        <TextField
          label="Redirect link after submission"
          value={settings.redirectLink || ""}
          onChange={(e) => handleSettingsChange("redirectLink", e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
        />

        {/* Button Text */}
        <Typography variant="body1" sx={{ mb: 1 }}>
          Button Text
        </Typography>
        <TextField
          label="Back button"
          value={settings.backButtonText || ""}
          onChange={(e) => handleSettingsChange("backButtonText", e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
        />
        <TextField
          label="Next button"
          value={settings.nextButtonText || ""}
          onChange={(e) => handleSettingsChange("nextButtonText", e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
        />
        <TextField
          label="Submit button"
          value={settings.submitButtonText || ""}
          onChange={(e) => handleSettingsChange("submitButtonText", e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
        />

        {/* End Message */}
        <TextField
          label="End message"
          value={settings.endMessage || ""}
          onChange={(e) => handleSettingsChange("endMessage", e.target.value)}
          fullWidth
          multiline
          rows={2}
          sx={{ mb: 2 }}
        />

        {/* Show Question Number */}
        <FormControlLabel
          control={
            <Switch
              checked={settings.showQuestionNumber || false}
              onChange={(e) => handleSettingsChange("showQuestionNumber", e.target.checked)}
            />
          }
          label="Show question number"
          sx={{ mb: 2 }}
        />

        {/* Show Progress Bar */}
        <FormControlLabel
          control={
            <Switch
              checked={settings.showProgressBar || false}
              onChange={(e) => handleSettingsChange("showProgressBar", e.target.checked)}
            />
          }
          label="Show progress bar"
          sx={{ mb: 2 }}
        />

        {/* Colors */}
        <Typography variant="body1" sx={{ mb: 1 }}>
          Colours
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2">Font Colour</Typography>
          <Box
            sx={{
              width: "100px",
              height: "30px",
              background: rgbaToCss(settings.fontColour),
              border: "1px solid #e0e0e0",
              borderRadius: "4px",
              cursor: "pointer",
            }}
            onClick={() => setShowFontColourPicker(true)}
          />
          {showFontColourPicker && (
            <Box ref={fontColourPickerRef} sx={{ position: "absolute", zIndex: 2 }}>
              <SketchPicker
                color={settings.fontColour || "rgba(0, 0, 0, 1)"}
                onChange={(color) => handleSettingsChange("fontColour", color.rgb)}
              />
              <Button
                onClick={() => setShowFontColourPicker(false)}
                sx={{ mt: 1 }}
              >
                Close
              </Button>
            </Box>
          )}
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2">Assessment Colour</Typography>
          <Box sx={{ display: "flex", gap: 1, mb: 1, alignItems: "center" }}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
              <Typography variant="caption" sx={{ fontSize: "0.7rem" }}>Start Color</Typography>
              <Box
                sx={{
                  width: "80px",
                  height: "30px",
                  background: rgbaToCss(settings.surveyColour?.start),
                  border: "1px solid #e0e0e0",
                  borderRadius: "4px",
                  cursor: "pointer",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                }}
                onClick={() => setShowSurveyColourPicker({ ...showSurveyColourPicker, start: true })}
              />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
              <Typography variant="caption" sx={{ fontSize: "0.7rem" }}>End Color</Typography>
              <Box
                sx={{
                  width: "80px",
                  height: "30px",
                  background: rgbaToCss(settings.surveyColour?.end),
                  border: "1px solid #e0e0e0",
                  borderRadius: "4px",
                  cursor: "pointer",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                }}
                onClick={() => setShowSurveyColourPicker({ ...showSurveyColourPicker, end: true })}
              />
            </Box>
          </Box>
          {/* Gradient Preview */}
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" sx={{ fontSize: "0.7rem", display: "block", mb: 0.5 }}>Gradient Preview:</Typography>
            <Box
              sx={{
                width: "100%",
                height: "20px",
                background: `linear-gradient(to right, ${rgbaToCss(settings.surveyColour?.start)}, ${rgbaToCss(settings.surveyColour?.end)})`,
                border: "1px solid #e0e0e0",
                borderRadius: "4px",
                boxShadow: "inset 0 1px 3px rgba(0,0,0,0.1)",
              }}
            />
          </Box>
          {showSurveyColourPicker.start && (
            <Box ref={startColorPickerRef} sx={{ position: "absolute", zIndex: 2 }}>
              <SketchPicker
                color={settings.surveyColour?.start || "rgba(255, 255, 255, 1)"}
                onChange={(color) => {
                  console.log("Start color changed:", color.rgb);
                  console.log("Current surveyColour:", settings.surveyColour);
                  const newSurveyColour = {
                    ...settings.surveyColour,
                    start: color.rgb,
                  };
                  console.log("New surveyColour object:", newSurveyColour);
                  handleSettingsChange("surveyColour", newSurveyColour);
                }}
              />
              <Button
                onClick={() => setShowSurveyColourPicker({ ...showSurveyColourPicker, start: false })}
                sx={{ mt: 1 }}
              >
                Close
              </Button>
            </Box>
          )}
          {showSurveyColourPicker.end && (
            <Box ref={endColorPickerRef} sx={{ position: "absolute", zIndex: 2 }}>
              <SketchPicker
                color={settings.surveyColour?.end || "rgba(255, 255, 255, 1)"}
                onChange={(color) => {
                  console.log("End color changed:", color.rgb);
                  console.log("Current surveyColour:", settings.surveyColour);
                  const newSurveyColour = {
                    ...settings.surveyColour,
                    end: color.rgb,
                  };
                  console.log("New surveyColour object:", newSurveyColour);
                  handleSettingsChange("surveyColour", newSurveyColour);
                }}
              />
              <Button
                onClick={() => setShowSurveyColourPicker({ ...showSurveyColourPicker, end: false })}
                sx={{ mt: 1 }}
              >
                Close
              </Button>
            </Box>
          )}
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2">Question Background</Typography>
            <Box
              sx={{
                width: "100px",
                height: "30px",
                background: rgbaToCss(settings.questionBackground),
                border: "1px solid #e0e0e0",
                borderRadius: "4px",
                cursor: "pointer",
              }}
              onClick={() => setShowQuestionBackgroundPicker(true)}
            />
            {showQuestionBackgroundPicker && (
              <Box ref={questionBackgroundPickerRef} sx={{ position: "absolute", zIndex: 2 }}>
                <SketchPicker
                  color={settings.questionBackground || "rgba(255, 255, 255, 1)"}
                  onChange={(color) => handleSettingsChange("questionBackground", color.rgb)}
                />
                <Button
                  onClick={() => setShowQuestionBackgroundPicker(false)}
                  sx={{ mt: 1 }}
                >
                  Close
                </Button>
              </Box>
            )}
        </Box>
      </Box>

      {/* Right Side: Preview */}
      <Box sx={{ flexGrow: 1 }}>
        <Box
          sx={{
            height: "500px",
            border: "1px solid #e0e0e0",
            borderRadius: "4px",
            background: settings.backgroundImage
              ? `url(${settings.backgroundImage}) no-repeat center/cover`
              : `linear-gradient(to right, ${rgbaToCss(settings.surveyColour?.start)}, ${rgbaToCss(settings.surveyColour?.end)})`,
            p: 3,
            color: rgbaToCss(settings.fontColour),
            position: "relative",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            ...(settings.backgroundImage && {
              filter: `brightness(${settings.backgroundBrightness || 100}%)`,
            }),
          }}
        >
          {/* Logo in Preview */}
          {settings.logo && (
            <Box
              sx={{
                position: "absolute",
                top: 16,
                ...(settings.logoAlignment?.toLowerCase() === "center"
                  ? { left: "50%", transform: "translateX(-50%)" }
                  : { [settings.logoAlignment?.toLowerCase() || "left"]: 16 }),
                padding: "8px",
                borderRadius: "8px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <img
                src={settings.logo}
                alt="Logo"
                style={{ maxWidth: "100px", maxHeight: "50px", objectFit: "contain" }}
              />
            </Box>
          )}

          {/* Content Wrapper */}
          <Box
            sx={{
              mt: settings.logo ? 10 : 4, // Space for logo (50px + padding) or default
              width: "100%",
              maxWidth: "600px", // Consistent with PreviewTab progress bar
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography variant="body1" sx={{ mb: 2, textAlign: "center" }}>
              Preview (Placeholder)
            </Typography>
            <Box
              sx={{
                backgroundColor: rgbaToCss(settings.questionBackground),
                p: 2,
                mb: 2,
                borderRadius: "4px",
                width: "100%",
              }}
            >
              <Typography variant="body2" sx={{ textAlign: "center" }}>
                Sample question background
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Button variant="contained" sx={{ mr: 1 }}>
                {settings.backButtonText || "Previous"}
              </Button>
              <Button variant="contained" sx={{ mr: 1 }}>
                {settings.nextButtonText || "Next"}
              </Button>
              <Button variant="contained">
                {settings.submitButtonText || "Submit"}
              </Button>
            </Box>
            <Typography variant="body2" sx={{ textAlign: "center" }}>
              {settings.endMessage || "Thank you for completing the assessment!"}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default SettingsTab;
